import { useAuth } from '@clerk/react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useDashboard } from '@/api/hooks';

// E2E test bypass: when the build is launched with VITE_E2E_AUTH_BYPASS=1
// (only set by playwright.config.ts), render the protected tree without
// gating on Clerk. The string check inlines to a constant at build time,
// so production bundles never include the bypass path.
const E2E_BYPASS = import.meta.env.VITE_E2E_AUTH_BYPASS === '1';

export function ProtectedRoute({ children }: { children: ReactNode }) {
  if (E2E_BYPASS) {
    return <NeedsOnboardingGate>{children}</NeedsOnboardingGate>;
  }

  // biome-ignore lint/correctness/useHookAtTopLevel: E2E_BYPASS is a build-time constant; the conditional resolves identically every render, so the hook call order is stable in practice.
  const { isSignedIn, isLoaded } = useAuth();

  if (!isLoaded) {
    return null; // Clerk is still loading — avoid flash
  }

  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  return <NeedsOnboardingGate>{children}</NeedsOnboardingGate>;
}

/**
 * Force a first-login user through `/onboarding` until they've named
 * their project. There's no separate `getProject` endpoint in v1, so we
 * use the dashboard's `projectName` as the "has project been set up?"
 * marker. The dashboard query is already cached by the rest of the app,
 * so this adds no extra round-trip on warm navigation.
 */
function NeedsOnboardingGate({ children }: { children: ReactNode }) {
  const location = useLocation();
  const dashboard = useDashboard();

  // E2E test escape hatch: the api-mock's `projectMeta` is process-global,
  // so when the F11 onboarding spec clears `projectName` it briefly bleeds
  // into other Playwright workers running in parallel and bounces unrelated
  // specs to /onboarding. The onboarding spec opts INTO gate enforcement by
  // setting `bouw.test.enforceOnboarding=1` in sessionStorage; all other
  // specs leave it unset and the gate becomes a no-op. Production builds
  // never hit this branch — `E2E_BYPASS` inlines to `false` there.
  if (E2E_BYPASS) {
    if (
      typeof sessionStorage === 'undefined' ||
      sessionStorage.getItem('bouw.test.enforceOnboarding') !== '1'
    ) {
      return <>{children}</>;
    }
  }

  // Already on onboarding — let it render and avoid a redirect loop.
  if (location.pathname === '/onboarding') {
    return <>{children}</>;
  }

  // Wait for the first read before deciding, so we don't flash the
  // dashboard then yank the user away.
  if (dashboard.isPending) {
    return null;
  }

  // If the dashboard read failed entirely, render the children — better
  // a broken dashboard with a visible error than an undebuggable
  // redirect to /onboarding.
  if (dashboard.isError || !dashboard.data) {
    return <>{children}</>;
  }

  if ((dashboard.data.projectName ?? '').trim() === '') {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}
